




















































































import { Cid, Post } from 'types'
import { Component, Mixins, Prop, Watch } from 'vue-property-decorator'
import { namespace, Getter, State } from 'vuex-class'
import BeethovenAttachmentPreview from 'vuecomponents/beethoven-attachment-preview.vue'
import BeethovenAttachmentCaption from 'vuecomponents/beethoven-attachment-caption.vue'
import SurfacePostAttachmentButton from 'vuecomponents/SurfacePostAttachmentButton.vue'
import { trackEvent } from 'bits/pepin'
import { postColorRgb, serializeRgbaStringToRgba, computeLuminanceRatio, RGB } from 'bits/post_color'
import { BeethovenDisplayAttributes } from '@padlet/beethoven-client'
import { BeethovenContentCategory, BeethovenContentSubcategory } from '@padlet/beethoven-client/lib/types'
import { addQueryParams } from 'bits/url'
import { getStylizedProviderName, downloadUrl, urlForOpening } from 'bits/attachments'
import attachmentCaptionForDisplay, { defaultLinkCaption } from 'surface/attachment_caption_for_display'
import { observeIntersection } from 'bits/intersection'
import { isAppUsing } from 'bits/flip'

const PHONE_SCREEN_WIDTH = 480
const MAX_STREAM_POST_WIDTH = 540
const MAX_CHAT_POST_WIDTH = 320
const MAX_TABLE_CELL_POST_WIDTH = 300

@Component({
  components: {
    BeethovenAttachmentPreview,
    BeethovenAttachmentCaption,
    SurfacePostAttachmentButton,
  },
})
export default class SurfacePostAttachmentNew extends Mixins() {
  $el!: HTMLDivElement
  $refs!: {
    embedWrapper: HTMLDivElement
    attachmentCaption: BeethovenAttachmentCaption
  }

  @Prop() post!: Post
  @Prop() isEditableByCurrentUser!: boolean
  @Prop() isWishContentScrollable!: boolean
  @Prop({ default: true }) actionable!: boolean
  @Getter isStream!: boolean
  @Getter format!: string
  @Getter colorScheme!: 'light' | 'dark'
  @Getter isAPostDragging!: boolean
  @namespace('browser').Getter windowWidth!: number
  @namespace('browser').Getter isSmallerThanDesktop!: boolean
  @namespace('attachment').Action setSurfacePreviewImageLoaded!: (payload: { url: string }) => void

  isImageLoaded = false
  isLoaded = false
  isError = false
  linkData: BeethovenDisplayAttributes | null = null
  isFallback = false
  showMeta = false

  contentCategory: BeethovenContentCategory | null = null
  contentSubcategory: BeethovenContentSubcategory | null = null
  downloadFileName: string | null = null
  attachmentAspectRatio = 0
  attachmentHeight = 0
  attachmentColor = 'rgb(0,0,0)'
  providerName = ''
  attachmentLuminance: any
  isDownloadable = false
  attachmentEmbedCode = ''
  errorText: string | null = null

  @State isMobile!: boolean

  public postMessage(event: string): void {
    switch (event) {
      case 'back-to-preview':
        this.backToPreview()
        return
      case 'zoom':
        this.zoom()
        return
      case 'open-in-new':
        this.openInNew()
        return
      default:
        return
    }
  }

  handleBeethovenError(error): void {
    this.isError = true
    this.isImageLoaded = true
    this.errorText = error
  }

  handleDataLoaded(data): void {
    this.errorText = null
    this.isLoaded = true
    this.isError = false
    this.linkData = data
    this.isFallback = data.is_fallback
    this.contentCategory = data.content_category
    this.contentSubcategory = data.content_subcategory
    this.attachmentEmbedCode = data.embed_code
    this.attachmentAspectRatio = data.image_aspect_ratio
    this.attachmentHeight = data.image_height
    this.attachmentColor = data.image_color
    this.attachmentLuminance = data.image_luminance
    this.isDownloadable = data.is_downloadable && !this.isMobile
    this.showMeta = data.show_meta
    this.providerName = getStylizedProviderName(data.provider_name)

    if (this.contentCategory === 'file') {
      this.downloadFileName = this.post.subject || null
    }

    this.$emit('load-data', data)
  }

  handleImageLoaded(data): void {
    this.isImageLoaded = true
    if (this.url) {
      this.setSurfacePreviewImageLoaded({ url: this.url })
    }
    this.$emit('load-image', data)
  }

  get isDataLoaded(): boolean {
    // We show the loading placeholder until we have `linkData` emitted from `beethoven-attachment-preview`.
    // For tweets, we don't rely on this `linkData` but instead we use `post.wish_content.attachment_props`.
    // See the comment in `beethoven-attachment-preview.vue`'s `getBeethovenAttributes` method for more details.
    return (!this.isError && !!this.linkData) || this.post.wish_content?.attachment_props?.type === 'tweet'
  }

  get downloadUrl(): string {
    return downloadUrl(this.urlForDisplay as string)
  }

  get urlForOpening(): string {
    // If attachment should be opened in expanded view, we set post permalink
    // as the URL so that cmd/ctrl clicking opens it in a new tab.
    if (this.interaction === 'expand' && this.post.permalink) {
      return this.post.permalink
    }
    return urlForOpening(this.linkData, this.urlForDisplay || '')
  }

  get defaultAttachmentCaption(): string {
    return defaultLinkCaption(this.linkData)
  }

  get hasCaption(): boolean {
    return !!attachmentCaptionForDisplay({
      postAttachmentCaption: this.captionText,
      defaultCaption: this.defaultAttachmentCaption,
    })
  }

  get url(): string | undefined {
    return this.post?.attachment
  }

  @Watch('url')
  handleUrlChanged(): void {
    this.isError = false
    this.isImageLoaded = false
    this.linkData = null
    this.isFallback = false
    this.showMeta = false
  }

  get urlForDisplay(): string | undefined {
    // Note: This should be used in display/href/... instead of this.url so if the attachment is censored (e.g. unsafe links), the bad attachment won't be exposed
    if (this.linkData?.url) return this.linkData?.url
    if (this.isError) return this.url
    return undefined
  }

  get captionText(): string | undefined | null {
    return this.post?.attachment_caption
  }

  get attachmentTitle(): string {
    return this.linkData?.title || ''
  }

  get attachmentDescription(): string {
    return this.linkData?.description || ''
  }

  get backgroundColor(): RGB {
    return postColorRgb({ postColor: this.post.color, isLightColorScheme: this.isLightColorScheme })
  }

  get imageBackgroundColor(): RGB {
    if (!this.attachmentColor) return this.backgroundColor
    return serializeRgbaStringToRgba(this.attachmentColor)
  }

  get imageBackgroundColorString() {
    const c = this.imageBackgroundColor
    return `rgb(${c.r},${c.g},${c.b})`
  }

  get isShowingAttachmentBorder(): boolean {
    const luminanceRatio = computeLuminanceRatio(this.backgroundColor, this.imageBackgroundColor)
    return luminanceRatio < 1.5
  }

  get isLightColorScheme(): boolean {
    return this.colorScheme === 'light'
  }

  get placeholderColorClass(): string {
    if (this.isDataLoaded) {
      return ''
    }

    // The pulse animation can sometimes be expensive in terms of CPU usage and style recalcs per second according to Google Chrome's performance monitor
    // => only show it when the attachment is visible inside the viewport
    if (!this.hasIntersectedViewport) {
      return ''
    }

    // pulse between post background color and 3 shade darker in light mode
    // pulse between post background color and 2 shade lighter in dark mode
    switch (this.post.color) {
      case 'red':
        return this.isLightColorScheme
          ? 'animate-pulse-bg-color-slow pulse-bg-from-scarlet-100 pulse-bg-to-scarlet-400'
          : 'animate-pulse-bg-color-slow pulse-bg-from-scarlet-900 pulse-bg-to-scarlet-700'
      case 'orange':
        return this.isLightColorScheme
          ? 'animate-pulse-bg-color-slow pulse-bg-from-canary-100 pulse-bg-to-canary-400'
          : 'animate-pulse-bg-color-slow pulse-bg-from-tangerine-900 pulse-bg-to-tangerine-700'
      case 'green':
        return this.isLightColorScheme
          ? 'animate-pulse-bg-color-slow pulse-bg-from-park-100 pulse-bg-to-park-400'
          : 'animate-pulse-bg-color-slow pulse-bg-from-park-900 pulse-bg-to-park-700'
      case 'blue':
        return this.isLightColorScheme
          ? 'animate-pulse-bg-color-slow pulse-bg-from-oceanic-100 pulse-bg-to-oceanic-400'
          : 'animate-pulse-bg-color-slow pulse-bg-from-oceanic-900 pulse-bg-to-oceanic-700'
      case 'purple':
        return this.isLightColorScheme
          ? 'animate-pulse-bg-color-slow pulse-bg-from-grape-100 pulse-bg-to-grape-400'
          : 'animate-pulse-bg-color-slow pulse-bg-from-grape-900 pulse-bg-to-grape-700'
      default:
        return this.isLightColorScheme
          ? 'animate-pulse-bg-color-slow pulse-bg-from-light-ui-100 pulse-bg-to-light-ui-400'
          : 'animate-pulse-bg-color-slow pulse-bg-from-dark-ui-100 pulse-bg-to-dark-ui-400'
    }
  }

  postWidth = 254

  get canvasPostWidth(): number | undefined {
    return this.post.width
  }

  @Watch('canvasPostWidth')
  onRealPostWidthChanged(value) {
    if (typeof value === 'number') {
      this.postWidth = this.canvasPostWidth as number
    }
  }

  @Watch('windowWidth', { immediate: true })
  onWindowWidthChanged() {
    this.$nextTick(() => {
      this.postWidth = this.getPostWidth()
    })
  }

  getPostWidth(): number {
    const gridSize = Math.ceil(this.$parent.$el.clientWidth || 0)
    const MAP_GRID_SIZE = 254
    const TIMELINE_POST_WIDTH = 254
    switch (this.format) {
      case 'stream':
        if (this.windowWidth <= PHONE_SCREEN_WIDTH) {
          return gridSize
        }
        return MAX_STREAM_POST_WIDTH
      case 'grid':
        return Math.ceil(gridSize)
      case 'shelf':
        return gridSize
      case 'matrix':
        return gridSize
      case 'chat':
        if (this.windowWidth <= MAX_CHAT_POST_WIDTH) {
          return gridSize
        }
        return MAX_CHAT_POST_WIDTH
      case 'table':
        return MAX_TABLE_CELL_POST_WIDTH
      case 'map':
        return MAP_GRID_SIZE
      case 'timeline':
        return TIMELINE_POST_WIDTH
      default:
        return Math.ceil(this.post.width || 0) || 1920
    }
  }

  get attachmentWidth(): number {
    return this.isStream ? this.postWidth - 24 : this.postWidth - 16
  }

  get realAttachmentHeight(): number {
    return this.attachmentHeight || this.attachmentWidth / this.attachmentAspectRatio
  }

  get isPadletAttachment(): boolean {
    // provider_name for a wall is the builder's name, "padlet" should be used only for landside pages (e.g. padlet.com, padlet.com/features)
    return this.contentSubcategory === 'padlet' && this.providerName.toLowerCase() !== 'padlet'
  }

  get interaction(): 'no-action' | 'play' | 'zoom' | 'download' | 'link' | 'expand' {
    if (this.linkData?.metadata?.attachment_props?.type === 'tweet') {
      // Ensure we never show the Tweet embed when using the new Tweet preview,
      // instead, we take the users to the tweet on Twitter.
      return 'link'
    }
    if (!this.actionable) {
      return 'no-action'
    }
    if (
      isAppUsing('expandedPostV1') &&
      this.contentCategory === 'audio' &&
      this.providerName.toLowerCase() !== 'padlet drive' &&
      !this.isStream
    ) {
      return 'expand'
    }
    if (isAppUsing('expandedPostV1') && this.providerName.toLowerCase() === 'tiktok') {
      return 'expand'
    }
    if (this.contentCategory === 'video' || this.contentCategory === 'audio') {
      return 'play'
    }
    if (this.isDownloadable && this.contentCategory === 'file') {
      return 'download'
    }
    if (isAppUsing('expandedPostV1') && this.contentCategory === 'photo') {
      return 'expand'
    }
    if (isAppUsing('expandedPostV1') && this.contentCategory === 'page') {
      return 'link'
    }
    if (isAppUsing('expandedPostV1') && this.attachmentEmbedCode && this.contentSubcategory !== 'padlet') {
      return 'expand'
    }

    if (
      !isAppUsing('expandedPostV1') &&
      ((this.isPhone && this.contentCategory === 'photo') ||
        (this.attachmentEmbedCode && this.contentSubcategory !== 'padlet'))
    ) {
      return 'zoom'
    }
    return 'link'
  }

  get buttonIcon(): string | null {
    if (this.contentCategory === 'video' || this.contentCategory === 'audio') {
      return 'play'
    }
    return null
  }

  @namespace('post').Action startEditingPost!: (payload: {
    postCid: Cid
    shouldFocusAttachmentCaption: boolean
  }) => void

  openPostModal(): void {
    if (this.isEditableByCurrentUser) {
      this.startEditingPost({ postCid: this.post.cid, shouldFocusAttachmentCaption: true })
    }
  }

  // * EMBEDS AND ACTIONS

  /**
   * True -- normal behavior of showing the preview
   * False -- showing something else (etc. video player, Spotify embed)
   */
  showPreview = true
  showEmbed = false
  embedHtml: string | null = null

  @namespace('postAction').State currentlyPlayingPostCid!: Cid
  @namespace('postAction').Action expandPost!: (payload: { postCid: Cid; xTextPanel?: boolean }) => void
  @namespace('postAction').Action zoomPost!: (payload: {
    zoomedPostCid: Cid
    zoomedLink: BeethovenDisplayAttributes
  }) => void
  @namespace('postAction').Action setCurrentlyPlayingPost!: (payload: { cid: Cid }) => void
  @namespace('postAction').Action unsetCurrentlyPlayingPost!: (payload: { cid: Cid }) => void

  @Watch('showEmbed')
  toggleEmbedControl(xEmbed): void {
    this.$emit('toggle-embed-control', {
      xEmbed,
      isDownloadable: this.isDownloadable,
      downloadUrl: this.downloadUrl,
    })
  }

  @Watch('currentlyPlayingPostCid')
  onCurrentlyPlayingPostCidChannge(newCid: Cid): void {
    if (!this.showPreview && newCid && this.post.cid !== newCid) this.backToPreview()
  }

  unsetAsCurrentlyPlayingPost() {
    if (this.currentlyPlayingPostCid === this.post.cid) {
      this.unsetCurrentlyPlayingPost({ cid: this.post.cid })
    }
  }

  embedInline(): void {
    // should set this to false first before calling setCurrentlyPlayingPost
    // so that in the watcher this.showPreview is false
    this.showPreview = false
    this.setCurrentlyPlayingPost({ cid: this.post.cid })
    this.showEmbed = true
    this.embedHtml = this.attachmentEmbedCode
    this.$nextTick(() => {
      const embedTag = this.$refs.embedWrapper.firstChild as Element
      if (embedTag.tagName.toLowerCase() === 'iframe') {
        const embedTagIframe = embedTag as HTMLIFrameElement
        // Set loading attributes: width, height and underlay background while it's loading
        embedTagIframe.setAttribute('width', this.attachmentWidth + 'px')
        embedTagIframe.setAttribute('height', this.realAttachmentHeight + 'px')
        embedTagIframe.style.background = this.attachmentColor

        // Override iframe for better behavior
        const currentSrc = embedTagIframe.getAttribute('src')
        let newSrc = addQueryParams(currentSrc, 'autoplay=1')
        // Need to set this for cross-origin autoplay to work, e.g. padlet.com/beethoven/play on starkindustries.padlet.org
        if (this.providerName.toLowerCase() === 'padlet drive') {
          embedTagIframe.setAttribute('allow', 'autoplay')
        }
        if (this.providerName.toLowerCase() === 'youtube') {
          newSrc = addQueryParams(newSrc, 'rel=0')
        }
        embedTagIframe.setAttribute('src', newSrc)
        this.embedHtml = embedTagIframe.outerHTML
      }
    })
  }

  zoom() {
    if (!this.linkData) return
    this.backToPreview()
    this.zoomPost({
      zoomedPostCid: this.post?.cid,
      zoomedLink: this.linkData,
    })
  }

  backToPreview() {
    this.showPreview = true
    this.showEmbed = false
    this.unsetAsCurrentlyPlayingPost()
    // return $(this.$el).parents('.wish').removeClass('is-playing')
  }

  openInNew() {
    if (this.isApp) {
      this.openLink({
        linkType: 'external',
        url: this.urlForDisplay,
        contentCategory: this.contentCategory,
        contentSubcategory: this.contentSubcategory,
      })
    } else {
      window.open(this.urlForDisplay)
    }
  }

  @namespace('nativeApp').Action openLink!: (payload: { url; linkType; contentCategory; contentSubcategory }) => void
  playInApp() {
    let linkType = 'external'
    if (this.contentSubcategory === 'padlet' && this.isPadletAttachment) {
      linkType = 'padlet'
    } else if (this.contentCategory === 'photo' || this.contentCategory === 'file') {
      linkType = 'internal_mediaplayer'
    } else if (this.contentCategory === 'page' || this.contentCategory === 'document') {
      linkType = 'internal_webview'
    }

    this.openLink({
      url: this.urlForDisplay,
      linkType,
      contentCategory: this.contentCategory,
      contentSubcategory: this.contentSubcategory,
    })
  }

  @State isApp!: boolean
  @State isMac!: boolean
  @State canPostMessage!: boolean
  @State isPhone!: boolean

  play(e: MouseEvent): boolean {
    if (this.$el.closest('.surface-post')?.classList.contains('noclick')) {
      // Prevent play events when the post is being dragged
      e.preventDefault()
      return false
    }

    if ((this.isMac && e.metaKey) || (!this.isMac && e.ctrlKey)) {
      // Prevent play events when cmd/ctrl clicking so the default behavior
      // is used: open in new tab.
      return false
    }

    if (this.interaction === 'no-action') {
      return false
    }

    if (this.interaction === 'play') {
      // opening link: content is video and audio so we display player
      this.embedInline()
      if (this.contentCategory === 'video') {
        trackEvent('click', { target: 'attachment-interaction-inline-play-video', wish_id: this.post.id })
      }
      if (this.contentCategory === 'audio') {
        trackEvent('click', { target: 'attachment-interaction-inline-play-audio', wish_id: this.post.id })
      }
      e.preventDefault()
      return false
    }

    if (this.isApp && this.canPostMessage) {
      // opening link: isApp so use postMessage to open link
      this.playInApp()
      return false
    }

    if (this.interaction === 'zoom') {
      // opening link: on phone, only images zoom
      // opening link: display preview for file type having the embed code
      this.zoom()
      e.preventDefault()
      return false
    }

    if (this.interaction === 'expand') {
      // expand post with text panel closed
      this.expandPost({ postCid: this.post.cid, xTextPanel: !this.isSmallerThanDesktop })
      e.preventDefault()
      trackEvent('click', { target: 'attachment-interaction-expand-post', wish_id: this.post.id })
      return false
    }

    return true
  }

  hasIntersectedViewport = false

  mounted() {
    if (!this.hasIntersectedViewport) {
      const observeInitiated = observeIntersection(this.$el, () => {
        this.hasIntersectedViewport = true
      })
      if (!observeInitiated) {
        this.hasIntersectedViewport = true
      }
    }
  }
}
